import { Box, Button, Card, Grid, Typography } from "@mui/material";
import Logo from "assets/images/side_nav_inkfree_logo.svg";
import { CustomInput } from "global/components";
import { CustomContactNumberInput } from "global/components/CustomContactNumberInput/CustomContactNumberInput";
import {
  convertResourceToObjectFormat,
  handleErrorMessage,
  isTruthy,
  trackSignupComplete,
} from "helpers/methods";
import { useEffect, useState } from "react";
import {
  registerFormField,
  registrationValidation,
} from "./AuthTypesAndValidation";
import Clarity from "@microsoft/clarity";
import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import viewpaths from "global/constants/ViewPathConstants";
import { RegisterUser } from "models/interfaces";
import { oAuthSignUp } from "./AuthService";
import history from "utils/history";
import { useAppDispatch } from "hooks/use-app-dispatch";
import { loginAction } from "redux/authSlice";
import { getAccountDisplayName } from "./authSession";
import defaultImage from "assets/icons1/avatars/default.png";
import { useLocation } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { mobile, signer, signerAccentBar } from "theme/colors";
import { authRef, refFont } from "./AuthReferenceStyles";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";

const OAuthRegister = (props: any) => {
  useTitle(title.REGISTER);
  const authProviders = props?.location?.state;
  const planIdFromURL = new URLSearchParams(useLocation().search).get("id");
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [oAuthUser] = useState<any>(
    isTruthy(authProviders) ? authProviders : undefined,
  );
  const [formFields, setFormFields] = useState(
    registerFormField(isTruthy(oAuthUser) ? oAuthUser?.email : ""),
  );
  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    if (isTruthy(oAuthUser)) {
      const fullName = oAuthUser?.name || "";
      const nameParts = fullName.trim().split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      setFormFields((prev) => ({
        ...prev,
        email: { ...prev.email, value: oAuthUser.email || "", error: "" },
        firstName: { ...prev.firstName, value: firstName, error: "" },
        lastName: { ...prev.lastName, value: lastName, error: "" },
      }));
    }
  }, [oAuthUser]);

  const createAccountUsingOAuth = async () => {
    try {
      if (handleValidation()) {
        let token = "";
        if (urls.PROD) {
          token = await executeRecaptcha!();
        }
        setLoading(true);
        const userData: RegisterUser = {
          pwd: formFields.password?.value,
          authToken: "",
          name: formFields.firstName?.value + " " + formFields.lastName?.value,
          contactNo: formFields.contact?.value,
          email: formFields.email?.value,
          role: "",
          resources: [],
          account: formFields.company?.value,
          company: formFields.company?.value,
          captchaToken: token,
          planId: planIdFromURL ?? strings.FREE_PLAN_ID,
          authProviders: oAuthUser.authProviders ?? [],
        };
        const user = await oAuthSignUp("googleauth", userData);
        // Signup succeeded — fire the GTM event tagged with the chosen plan.
        trackSignupComplete(userData.planId);
        if (urls.PROD) {
          Clarity.setTag("account", userData.account);
          Clarity.setTag("email", userData.email);
          Clarity.identify(userData.email, userData.account, userData.account);
        }
        if (user?.authToken) {
          const formattedResources = convertResourceToObjectFormat(
            user.resources || {},
          );
          dispatch(
            loginAction({
              authenticated: true,
              accessToken: user.authToken,
              refreshToken: user.refreshToken,
              userName: user.name,
              email: (user.email ?? "").toLowerCase(),
              userRole: user.role,
              resources: formattedResources,
              profileImage: user.profilePicture
                ? "data:image/png;base64," + user.profilePicture
                : defaultImage,
              deviceId: "",
              account: user.account,
              accountDisplayName: getAccountDisplayName(user),
              userId: user.id,
              isTrial: user.trial,
              lastLogin: user.lastLogin,
            }),
          );
          history.push(viewpaths.dashboardViewPath);
        }
        setLoading(false);
      }
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOnchange = (event: React.ChangeEvent<any>) => {
    setFormFields({
      ...formFields,
      [event.target.name]: {
        ...formFields[event.target.name],
        value: event.target.value,
        error: "",
      },
    });
  };

  const handleValidation = () => {
    const { errors, isValid } = registrationValidation(formFields, true);
    setFormFields({ ...errors });
    return isValid;
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      createAccountUsingOAuth();
    }
  };

  return (
    <Box
      component="main"
      id="main-content"
      tabIndex={-1}
      sx={{
        minHeight: "100vh",
        backgroundColor: authRef.pageBg,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 5,
        boxSizing: "border-box",
        outline: "none",
      }}
    >
      {/* Brand accent bar across the top of the canvas, per reference 3c. */}
      <Box
        aria-hidden="true"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "5px",
          background: signerAccentBar,
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      />

      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 480,
          borderRadius: "16px",
          border: `1px solid ${authRef.cardBorder}`,
          boxShadow: authRef.cardShadow,
          padding: { xs: "28px 24px 24px", sm: "40px 48px 36px" },
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }}
      >
        <Box
          component="img"
          src={Logo}
          alt="Inkfree e-Sign"
          sx={{ width: 106, height: "auto", display: "block", mx: "auto" }}
        />
        <Typography
          component="h1"
          sx={{
            color: mobile.base[500],
            fontSize: refFont.cardHeading,
            fontWeight: 700,
            textAlign: "center",
            mt: 3,
          }}
        >
          Almost there
          {formFields.firstName.value ? `, ${formFields.firstName.value}` : ""}
        </Typography>
        <Typography
          sx={{
            color: authRef.subText,
            fontSize: refFont.cardBody,
            lineHeight: 1.6,
            textAlign: "center",
            mt: 1,
          }}
        >
          Just a couple of details and your workspace is ready.
        </Typography>

        {/* Signed-in-with-Google chip */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            backgroundColor: authRef.inputBg,
            border: `1px solid ${authRef.chipBorder}`,
            borderRadius: "10px",
            padding: "12px 16px",
            mt: 3,
          }}
        >
          <Box
            component="svg"
            aria-hidden="true"
            viewBox="0 0 48 48"
            sx={{ width: 18, height: 18, flexShrink: 0 }}
          >
            <path
              fill="#EA4335"
              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            />
            <path
              fill="#4285F4"
              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            />
            <path
              fill="#FBBC05"
              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            />
            <path
              fill="#34A853"
              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: refFont.label,
                fontWeight: 700,
                color: mobile.base[500],
                textAlign: "left",
              }}
            >
              Signed in with Google
            </Typography>
            <Typography
              sx={{
                fontSize: refFont.cardMeta,
                color: authRef.subText,
                textAlign: "left",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {oAuthUser?.email || ""}
            </Typography>
          </Box>
          <Box
            aria-hidden="true"
            sx={{
              ml: "auto",
              flexShrink: 0,
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: authRef.successBadgeBg,
              color: authRef.successBadgeIcon,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4.5 12.5l5 5L20 6.5" />
            </svg>
          </Box>
        </Box>

        <Grid
          container
          spacing={2}
          sx={{
            mt: 0.75,
            // Field chrome per reference 3c: 46px tall, 8px radius, pale fill,
            // 14px inset. `&&` outranks CustomInput's !important rules.
            "&& .MuiOutlinedInput-root": {
              minHeight: "46px",
              height: "46px",
              borderRadius: "8px",
              padding: "0 14px !important",
              backgroundColor: authRef.inputBg,
              fontSize: refFont.body,
              fontWeight: 500,
              color: mobile.base[500],
              "& fieldset": { borderColor: authRef.inputBorder },
              "&:hover fieldset": { borderColor: authRef.inputBorderHover },
            },
            "&& .MuiInputBase-input": { padding: "0 !important" },
            "&& .MuiInputBase-input::placeholder": {
              color: authRef.metaText,
              opacity: 1,
            },
            "&& .MuiInputLabel-root": {
              fontSize: refFont.label,
              fontWeight: 600,
              color: mobile.base[500],
              marginLeft: 0,
              "& .MuiFormLabel-asterisk": { color: signer.orange.main },
            },
          }}
        >
            <Grid item xs={12} sm={6}>
              <CustomInput
                label="First Name"
                required
                placeHolder="Enter your first name"
                type="text"
                name="firstName"
                id="firstName"
                value={formFields.firstName.value}
                propsToInputElement={{ maxlength: 20 }}
                onChange={handleOnchange}
                onKeyPress={handleKeypress}
                error={
                  !isTruthy(formFields.firstName.value) &&
                  formFields.firstName.error
                }
                helperText={
                  !isTruthy(formFields.firstName.value)
                    ? formFields.firstName.error
                    : ""
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <CustomInput
                label="Last Name"
                required
                id="lastName"
                placeHolder="Enter your last name"
                type="text"
                name="lastName"
                value={formFields.lastName.value}
                propsToInputElement={{ maxlength: 20 }}
                onChange={handleOnchange}
                onKeyPress={handleKeypress}
                error={
                  !isTruthy(formFields.lastName.value) &&
                  formFields.lastName.error
                }
                helperText={
                  !isTruthy(formFields.lastName.value)
                    ? formFields.lastName.error
                    : ""
                }
              />
            </Grid>
            <Grid item xs={12}>
              <CustomContactNumberInput
                label="Contact Number"
                required
                id="contactNumber"
                value={formFields.contact?.value}
                placeHolder="Enter contact number"
                onChange={(phone: any) => {
                  setFormFields({
                    ...formFields,
                    contact: {
                      value: phone,
                      error: "",
                    },
                  });
                }}
                error={
                  isTruthy(formFields.contact.error) && formFields.contact.error
                }
              />
            </Grid>
            <Grid item xs={12}>
              <CustomInput
                label="Company Name"
                required
                id="companyName"
                placeHolder="Enter your company name"
                type="text"
                name="company"
                value={formFields.company.value}
                propsToInputElement={{ maxlength: 25 }}
                onChange={handleOnchange}
                onKeyPress={handleKeypress}
                error={
                  !isTruthy(formFields.company.value) &&
                  formFields.company.error
                }
                helperText={
                  !isTruthy(formFields.company.value)
                    ? formFields.company.error
                    : ""
                }
              />
              <Typography
                sx={{
                  mt: 0.75,
                  fontSize: refFont.legal,
                  color: authRef.metaText,
                }}
              >
                Shown on the sign requests you send.
              </Typography>
            </Grid>
          </Grid>

          <Button
            size="large"
            fullWidth
            sx={{
              mt: 3.25,
              height: "48px",
              borderRadius: "8px",
              fontWeight: 700,
              textTransform: "none",
              fontSize: refFont.cardButton,
              letterSpacing: 0,
            }}
            type="submit"
            variant="contained"
            onClick={() => createAccountUsingOAuth()}
            disabled={loading}
          >
            Finish setting up
          </Button>
      </Card>

      <Typography
        sx={{
          mt: 2.5,
          fontSize: refFont.cardFooter,
          color: authRef.footerText,
          textAlign: "center",
        }}
      >
        © {new Date().getFullYear()} Inkfree. All rights reserved.
      </Typography>
    </Box>
  );
};

export default OAuthRegister;
